// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    getStatus,
    IComputeResourceResponse,
    VIRTUALIZATION_TYPE_TRANSLATION_MAP,
    VirtualizationType,
} from 'common/api/resources/ComputeResource';
import {
    Action,
    List,
    Switch,
    Translate,
} from '@plesk/ui-library';
import {
    COMPUTE_RESOURCE,
    COMPUTE_RESOURCES,
} from 'admin/computeResource/constants/tests';
import CopyText from 'common/containers/CopyText/CopyText';
import { EmptyView } from 'common/components/EmptyView/EmptyView';
import { ActionsCell } from 'common/components/ActionsCell/ActionsCell';
import { ReactComponent as DetachStorageIcon } from 'common/icons/detach-storage.svg';
import { dataCySelector } from 'common/tests/selectors';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { Status } from 'admin/computeResource/common/Status/Status';
import { HaStatus } from 'admin/computeResource/common/HaStatus/HaStatus';
import { getActionColumnProps } from 'common/helpers/list';
import { ICONS } from 'common/constants';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as computeResourceActions from 'admin/computeResource/actions';
import { connect } from 'react-redux';
import PopoverList from 'common/components/PopoverList/PopoverList';
import { ILocationResponse } from 'common/api/resources/Location';
import LocationCell from 'common/components/IconCell/LocationCell';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import { StorageAlert } from 'admin/computeResource/common/StorageAlert/StorageAlert';
import { VirtualizationTypesContainer } from 'admin/computeResource/list/containers/ComputeResourceTable/Styles';
import { RootState } from 'admin/core/store';

const getColumns = () => [{
    width: '1%',
    key: 'colId',
    title: <Translate content="computeResource.list.id" />,
}, {
    key: 'colNameAndHost',
    title: <Translate content="computeResource.list.nameAndHost" />,
}, {
    key: 'colStatus',
    title: <Translate content="computeResource.list.status" />,
}, {
    key: 'colHaStatus',
    title: <Translate content="computeResource.list.haReadinessStatus" />,
}, {
    key: 'colFailoverDomain',
    title: <Translate content="computeResource.list.failoverDomain" />,
}, {
    key: 'colVersion',
    title: <Translate content="computeResource.list.version" />,
}, {
    key: 'colLocations',
    title: <Translate content="computeResource.list.locations" />,
}, {
    key: 'colServersAndHa',
    title: <Translate content="computeResource.list.serversAndHa" />,
}, {
    width: '1%',
    key: 'colLocked',
    title: <Translate content="computeResource.list.isLocked" />,
}, {
    width: '1%',
    key: 'colIsInMaintenance',
    title: <Translate content="computeResource.list.isInMaintenance" />,
    truncate: true,
}, getActionColumnProps(),
];

export interface IComputeResourceTableProps extends RouteComponentProps {
    items: IComputeResourceResponse[];
    onCreate: () => void;
    onEdit: (id: number) => void;
    onRemove: (id: number) => void;
    onSettingsClick: (id: number) => void;
    onDetailsClick: (id: number) => void;
    // When set, the row action becomes "Detach from storage" (detaching the CR
    // from this storage) instead of "Delete" — used on the Storage Overview page.
    storageId?: number;
    // Mirrors the CR Storages-tab detach button: disabled for VZ (local) storage.
    isDetachStorageDisabled?: boolean;
    // Called after a successful detach so the page can refresh counts.
    onDetached?: () => void | Promise<void>;
}

export type ComputeResourceTableProps =
    IComputeResourceTableProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const ComputeResourceTable: React.FC<ComputeResourceTableProps> = ({
    items,
    onCreate,
    onEdit,
    onRemove,
    onSettingsClick,
    onDetailsClick,
    storageId,
    isDetachStorageDisabled,
    onDetached,
    computeResourceActions: {
        updateIsLocked,
        updateIsInMaintenance,
        detachComputeResourceFromStorage,
    },
}) => {
    const columns = getColumns();
    const [lockingChanging, setLockingChanging] = React.useState<number[]>([]);
    const [maintenanceModeChanging, setMaintenanceModeChanging] = React.useState<number[]>([]);
    const [detachingChanging, setDetachingChanging] = React.useState<number[]>([]);

    const handleDetailsClick = (id: number) => () => onDetailsClick(id);
    const handleRemove = (id: number) => () => onRemove(id);
    const handleDetachFromStorage = (id: number) => async () => {
        if (storageId === undefined || detachingChanging.includes(id)) {
            return;
        }

        setDetachingChanging(prev => [...prev, id]);
        try {
            await detachComputeResourceFromStorage(id, storageId);
            await onDetached?.();
        } finally {
            setDetachingChanging(prev => prev.filter(crId => crId !== id));
        }
    };
    const handleEdit = (id: number) => () => onEdit(id);
    const handleSettingsClick = (id: number) => () => onSettingsClick(id);
    const handleToggleIsLocked = (cr: IComputeResourceResponse) => async() => {
        if (lockingChanging.includes(cr.id)) {
            return;
        }

        setLockingChanging(prev => [...prev, cr.id]);
        try {
            await updateIsLocked(cr.id, !cr.is_locked);
        } finally {
            setLockingChanging(prev => prev.filter(id => id !== cr.id));
        }
    };

    const handleToggleIsInMaintenance = (cr: IComputeResourceResponse) => async() => {
        if (maintenanceModeChanging.includes(cr.id)) {
            return;
        }

        setMaintenanceModeChanging(prev => [...prev, cr.id]);
        try {
            await updateIsInMaintenance(cr.id, !cr.is_in_maintenance);
        } finally {
            setMaintenanceModeChanging(prev => prev.filter(id => id !== cr.id));
        }
    };

    const buttonToComputeResource = (id: number, title: string) => (
        <Action
            component={Link}
            to={pathTo(`compute_resources/${id}`)}
        >
            {title}
        </Action>
    );

    const renderVirtualizationTypes = (virtualizationTypes: VirtualizationType[]) =>
        virtualizationTypes
            .map(type => VIRTUALIZATION_TYPE_TRANSLATION_MAP[type])
            .join(', ');

    const renderLocation = (location: ILocationResponse, index: number) => (
        <LocationCell location={location} adaptive={index === 0}/>
    );

    const data = items.map((item) => ({
        colId: buttonToComputeResource(item.id, item.id.toString()),
        colNameAndHost: (
            <>
                <div>
                    <Link to={pathTo(`compute_resources/${item.id}`)} style={{ fontWeight: 600 }}>
                        {item.name}
                    </Link>
                </div>

                <div>
                    <CopyText>{item.host}</CopyText>
                </div>
            </>
        ),
        colStatus: (
            <>
                <Status status={getStatus(item.status)} onDetailsClick={handleDetailsClick(item.id)} />

                <StorageAlert computeResource={item} />
            </>
        ),
        colHaStatus: item.ha_readiness_status ? (
            <HaStatus status={item.ha_readiness_status} />
        ) : undefined,
        colFailoverDomain: !!item.failover_domain && (
            <Link to={pathTo(`failover_domains/${item.failover_domain.id}`)}>
                {item.failover_domain.name}
            </Link>
        ),
        colVersion: item.version,
        colServersAndHa: (
            <>
                <div>
                    {item.vms_count}
                    {` (${item.high_availability_enabled_servers_count ?? 0})`}
                </div>

                <VirtualizationTypesContainer>
                    {renderVirtualizationTypes(item.settings.virtualization_types)}
                </VirtualizationTypesContainer>
            </>
        ),
        colLocations: (
            <PopoverList
                items={item.locations}
                render={renderLocation}
                keyExtractor={l => l.id.toString()}
            />
        ),
        colLocked: <Switch
            intent='danger'
            checked={item.is_locked}
            onChange={handleToggleIsLocked(item)}
            loading={lockingChanging.includes(item.id)}
            data-cy={dataCySelector(item.id, COMPUTE_RESOURCE.IS_LOCKED)}
        />,
        colIsInMaintenance: <Switch
            intent='danger'
            checked={item.is_in_maintenance}
            onChange={handleToggleIsInMaintenance(item)}
            loading={maintenanceModeChanging.includes(item.id)}
            data-cy={dataCySelector(item.id, COMPUTE_RESOURCE.IS_IN_MAINTENANCE)}
        />,
        colActions: (
            <ActionsCell
                triggerTooltip={<Translate content="computeResource.actions.menu" />}
                actions={[
                    {
                        icon: ICONS.GEAR,
                        label: <Translate content="computeResource.actions.settings" />,
                        onClick: handleSettingsClick(item.id),
                        'data-cy': dataCySelector(item.id, COMPUTE_RESOURCE.SETTINGS),
                    },
                    {
                        icon: ICONS.PENCIL,
                        label: <Translate content="computeResource.actions.edit" />,
                        onClick: handleEdit(item.id),
                        'data-cy': dataCySelector(item.id, COMPUTE_RESOURCE.EDIT),
                    },
                    storageId !== undefined ? {
                        icon: <DetachStorageIcon />,
                        label: <Translate content="computeResource.actions.detachFromStorage" />,
                        disabled: isDetachStorageDisabled,
                        'data-cy': dataCySelector(item.id, 'detachStorage'),
                        confirmation: {
                            isLoading: detachingChanging.includes(item.id),
                            handleConfirm: handleDetachFromStorage(item.id),
                            button: <Translate content="computeResource.detachStorage.button" />,
                            title: <Translate content="computeResource.detachStorage.title" />,
                            tooltip: <Translate content="computeResource.detachStorage.tooltip" />,
                        },
                    } : {
                        icon: ICONS.RECYCLE,
                        label: <Translate content="computeResource.actions.remove" />,
                        disabled: item.vms_count > 0,
                        'data-cy': dataCySelector(item.id, COMPUTE_RESOURCE.DELETE),
                        confirmation: {
                            isLoading: item.is_deleting,
                            handleConfirm: handleRemove(item.id),
                            button: <Translate content="computeResource.buttonWithConfirmation.button" />,
                            title: <Translate content="computeResource.buttonWithConfirmation.title" />,
                            tooltip: <Translate content="computeResource.buttonWithConfirmation.tooltip" />,
                        },
                    },
                ]}
            />
        ),
        key: item.id.toString(),
    }));

    return (
        <List
            emptyView={
                <EmptyView
                    title="computeResource.emptyView.title"
                    description="computeResource.emptyView.description"
                    buttonText="computeResource.emptyView.buttonText"
                    onButtonClick={onCreate}
                    icon="resource"
                />
            }
            data-cy={COMPUTE_RESOURCES.TABLE}
            columns={columns}
            data={data}
        />
    );
};

const mapStateToProps = (_state: RootState) => ({
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ComputeResourceTable));
